import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { ensureCheckinToken } from '../../services/tourService';
import { fmtDateShort } from '../../utils/tourSchedule';
import './TicketQR.css';

// The rider's check-in ticket: a QR the organizer scans on the day. The QR
// encodes "MTR1.<tourId>.<uid>.<token>" — the token is the registration secret.
export function TicketQR({ tour, uid, registration }) {
  const [dataUrl, setDataUrl] = useState(null);
  const [error, setError] = useState(false);
  // Recurring tours have one registration per booked date, so the ticket is
  // keyed by the registration id rather than the rider's uid.
  const regId = registration?.id || uid;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = await ensureCheckinToken(tour.id, regId);
        if (!token) { if (alive) setError(true); return; }
        const payload = `MTR1.${tour.id}.${regId}.${token}`;
        const url = await QRCode.toDataURL(payload, { width: 240, margin: 1, errorCorrectionLevel: 'M' });
        if (alive) setDataUrl(url);
      } catch {
        if (alive) setError(true);
      }
    })();
    return () => { alive = false; };
  }, [tour.id, regId]);

  const dateLabel = registration?.tourDate
    ? fmtDateShort(registration.tourDate)
    : (tour.startDate ? fmtDateShort(tour.startDate) : null);

  if (registration?.checkedIn) {
    return (
      <div className="tqr tqr--done">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
        <div>
          <div className="tqr__done-title">Checked in</div>
          <div className="tqr__done-sub">Enjoy the ride!</div>
        </div>
      </div>
    );
  }

  // Compose a self-contained ticket image (QR + tour + date) and download it,
  // so the rider has it offline even with no signal at the meeting point.
  function saveTicket() {
    if (!dataUrl) return;
    const W = 640, H = 860, PAD = 48;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ff7a1a';
      ctx.font = 'bold 28px system-ui, -apple-system, Segoe UI, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('MOTOROAM', W / 2, 72);

      const qr = W - PAD * 2;
      ctx.drawImage(img, PAD, 120, qr, qr);

      // Tour name — wrapped to at most two lines.
      ctx.fillStyle = '#1e2430';
      ctx.font = 'bold 30px system-ui, -apple-system, Segoe UI, sans-serif';
      const words = String(tour.name || 'Tour').split(' ');
      const lines = [];
      let line = '';
      for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > W - PAD * 2 && line) { lines.push(line); line = w; }
        else line = test;
        if (lines.length === 2) break;
      }
      if (line && lines.length < 2) lines.push(line);
      let y = 160 + qr;
      lines.forEach(l => { ctx.fillText(l, W / 2, y); y += 38; });

      if (dateLabel) {
        ctx.fillStyle = '#6b7280';
        ctx.font = '24px system-ui, -apple-system, Segoe UI, sans-serif';
        ctx.fillText(dateLabel, W / 2, y + 6);
      }

      ctx.fillStyle = '#9aa1ac';
      ctx.font = '20px system-ui, -apple-system, Segoe UI, sans-serif';
      ctx.fillText('Show this to the organizer to check in', W / 2, H - 40);

      const safe = String(tour.name || 'tour').replace(/[^a-z0-9]+/gi, '-').toLowerCase().slice(0, 40);
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `motoroam-ticket-${safe}.png`;
      a.click();
    };
    img.src = dataUrl;
  }

  return (
    <div className="tqr">
      <div className="tqr__label">Your check-in ticket</div>
      <div className="tqr__code">
        {error
          ? <div className="tqr__err">Couldn’t load your ticket QR. Try reopening this tour.</div>
          : dataUrl
            ? <img src={dataUrl} alt="Check-in QR code" width={200} height={200} />
            : <div className="tqr__ph" />}
      </div>
      <div className="tqr__meta">
        <span className="tqr__meta-name">{tour.name}</span>
        {dateLabel && <span className="tqr__meta-date">{dateLabel}</span>}
        {registration?.lessonStartTime && (
          <span className="tqr__meta-date">
            {registration.lessonStartTime} · {registration.lessonHours}h{registration.lessonRentBike ? ' · Bike hire' : ''}
          </span>
        )}
      </div>
      {dataUrl && !error && (
        <button className="tqr__save" onClick={saveTicket}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Save ticket
        </button>
      )}
      <p className="tqr__hint">Show this to the organizer at the start to check in.</p>
    </div>
  );
}
